import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  TableCellLayout,
  TableColumnDefinition,
  Text,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Delete24Regular,
  Edit24Regular,
  DocumentBulletList24Regular,
  CalendarLtr24Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ContractNegotiation } from '../../../models/Entities/Contracts/ContractNegotiation';
import { NewContractNegotiation } from '../../../models/Entities/Contracts/NewContractNegotiation';
import * as ComponentFunctions from './ContractNegotiationList.Functions';
import { useContractNegotiationListStyles } from './ContractNegotiationList.Styles';
import ContractNegotiationFormComponent from '../ContractNegotiationForm/ContractNegotiationForm.Component';
import ContractNegotiationConceptListComponent from '../ContractNegotiationConceptList/ContractNegotiationConceptList.Component';
import ContractNegotiationVersionListComponent from '../ContractNegotiationVersionList/ContractNegotiationVersionList.Component';
import { mapStateToProps, mapDispatchToProps } from './ContractNegotiationList.Functions';

type BadgeColor = 'warning' | 'brand' | 'success' | 'danger' | 'informative';

interface NegotiationStatusInfo {
  label: string;
  color: BadgeColor;
}

const getNegotiationStatusInfo = (
  status: number,
  t: (key: string, fallback: string) => string
): NegotiationStatusInfo => {
  if (status === 0) return { label: t('contractNegotiationList.status.pending', 'Pendiente'), color: 'warning' };
  if (status === 1) return { label: t('contractNegotiationList.status.inProgress', 'En curso'), color: 'brand' };
  if (status === 2) return { label: t('contractNegotiationList.status.finished', 'Finalizada'), color: 'success' };
  if (status === 3) return { label: t('contractNegotiationList.status.cancelled', 'Cancelada'), color: 'danger' };
  return { label: String(status), color: 'informative' };
};

const formatDate = (date: Date | undefined | string | null): string => {
  if (!date) return '-';
  return new Date(date as string).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const ContractNegotiationListComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useContractNegotiationListStyles();

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [selectedNegotiation, setSelectedNegotiation] = useState<ContractNegotiation | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<ContractNegotiation | undefined>(undefined);
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(undefined);

  useEffect(() => {
    props.GetContractNegotiationsByAddendumId(props.contractId, props.contractAddendumId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.contractId, props.contractAddendumId]);

  const handleAddNew = () => {
    setSelectedNegotiation(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (negotiation: ContractNegotiation) => {
    setSelectedNegotiation(negotiation);
    setIsFormOpen(true);
  };

  const handleFormDismiss = () => {
    setIsFormOpen(false);
    setSelectedNegotiation(undefined);
  };

  const handleSave = (entity: NewContractNegotiation | ContractNegotiation) => {
    const isExisting = 'id' in entity && !!(entity as ContractNegotiation).id;
    if (isExisting) {
      props.UpdateContractNegotiation(entity as ContractNegotiation);
    } else {
      props.CreateContractNegotiation(entity as NewContractNegotiation);
    }
    setIsFormOpen(false);
    setSelectedNegotiation(undefined);
  };

  const handleDeleteRequest = (negotiation: ContractNegotiation) => {
    setDeleteTarget(negotiation);
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      props.DeleteContractNegotiation(
        deleteTarget.id,
        deleteTarget.contractId,
        deleteTarget.contractAddendumId
      );
    }
    setDeleteTarget(undefined);
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(undefined);
  };

  const toggleExpand = (id: string) => {
    setSelectedRowId((prev) => {
      const next = prev === id ? undefined : id;
      if (props.onNegotiationSelected) {
        props.onNegotiationSelected(next);
      }
      return next;
    });
  };

  const columns: TableColumnDefinition<ContractNegotiation>[] = useMemo(
    () => [
      createTableColumn<ContractNegotiation>({
        columnId: 'sponsorName',
        compare: (a, b) => (a.sponsorName || '').localeCompare(b.sponsorName || ''),
        renderHeaderCell: () => t('contractNegotiationList.columns.sponsor', 'Sponsor'),
        renderCell: (item) => (
          <TableCellLayout>{item.sponsorName || item.sponsorId || '-'}</TableCellLayout>
        ),
      }),
      createTableColumn<ContractNegotiation>({
        columnId: 'negotiationStart',
        renderHeaderCell: () => t('contractNegotiationList.columns.start', 'Inicio'),
        renderCell: (item) => (
          <TableCellLayout media={<CalendarLtr24Regular />}>
            {formatDate(item.negotiationStart)}
          </TableCellLayout>
        ),
      }),
      createTableColumn<ContractNegotiation>({
        columnId: 'negotiationEnd',
        renderHeaderCell: () => t('contractNegotiationList.columns.end', 'Fin'),
        renderCell: (item) => <TableCellLayout>{formatDate(item.negotiationEnd)}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiation>({
        columnId: 'negotiationStatus',
        renderHeaderCell: () => t('contractNegotiationList.columns.status', 'Estado'),
        renderCell: (item) => {
          const info = getNegotiationStatusInfo(item.negotiationStatus, t);
          return (
            <TableCellLayout>
              <Badge appearance="tint" color={info.color} className={styles.statusBadge}>
                {info.label}
              </Badge>
            </TableCellLayout>
          );
        },
      }),
      createTableColumn<ContractNegotiation>({
        columnId: 'actions',
        renderHeaderCell: () => t('contractNegotiationList.columns.actions', 'Acciones'),
        renderCell: (item) => (
          <TableCellLayout>
            <div className={styles.actionCell}>
              <Button
                appearance="subtle"
                size="small"
                icon={<Edit24Regular />}
                onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                title={t('contractNegotiationList.actions.edit', 'Editar')}
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete24Regular />}
                onClick={(e) => { e.stopPropagation(); handleDeleteRequest(item); }}
                title={t('contractNegotiationList.actions.delete', 'Eliminar')}
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<DocumentBulletList24Regular />}
                onClick={(e) => { e.stopPropagation(); toggleExpand(item.id); }}
                title={t('contractNegotiationList.actions.expand', 'Ver detalles')}
              />
            </div>
          </TableCellLayout>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, styles]
  );

  const isLoading = props.isLoading;
  const hasError = !!props.errorMessage;

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Text weight="semibold" size={400}>
          {t('contractNegotiationList.title', 'Negociaciones')}
        </Text>
        <Button appearance="primary" icon={<Add24Regular />} onClick={handleAddNew}>
          {t('contractNegotiationList.addButton', 'Nueva negociación')}
        </Button>
      </div>

      {isLoading && <Spinner label={t('contractNegotiationList.loading', 'Cargando negociaciones...')} />}

      {hasError && (
        <MessageBar intent="error">
          <MessageBarBody>{props.errorMessage}</MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !hasError && props.negotiations.length === 0 && (
        <div className={styles.emptyState}>
          <Text>{t('contractNegotiationList.empty', 'No hay negociaciones registradas.')}</Text>
        </div>
      )}

      {props.negotiations.length > 0 && (
        <div className={styles.gridWrapper}>
          <DataGrid
            items={props.negotiations}
            columns={columns}
            sortable
            focusMode="composite"
            getRowId={(item) => item.id}
            style={{ width: '100%', minWidth: 0 }}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<ContractNegotiation>>
              {({ item, rowId }) => (
                <React.Fragment key={rowId}>
                  <DataGridRow<ContractNegotiation>
                    className={`${styles.clickableRow}${selectedRowId === item.id ? ` ${styles.selectedRow}` : ''}`}
                    onClick={() => toggleExpand(item.id)}
                  >
                    {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                  </DataGridRow>
                  {selectedRowId === item.id && (
                    <div className={styles.fullRow}>
                      {/* <Accordion multiple collapsible>
                        <AccordionItem value="concepts">
                          <AccordionHeader>
                            {t('contractNegotiationList.accordion.concepts', 'Conceptos de negociación')}
                          </AccordionHeader>
                          <AccordionPanel>
                            <ContractNegotiationConceptListComponent
                              contractNegotiationId={item.id}
                              contractId={props.contractId}
                            />
                          </AccordionPanel>
                        </AccordionItem>
                        <AccordionItem value="versions">
                          <AccordionHeader>
                            {t('contractNegotiationList.accordion.versions', 'Versiones de negociación')}
                          </AccordionHeader>
                          <AccordionPanel>
                            <ContractNegotiationVersionListComponent
                              contractNegotiationId={item.id}
                              contractId={props.contractId}
                            />
                          </AccordionPanel>
                        </AccordionItem>
                      </Accordion> */}
                    </div>
                  )}
                </React.Fragment>
              )}
            </DataGridBody>
          </DataGrid>
        </div>
      )}

      <ContractNegotiationFormComponent
        contractId={props.contractId}
        contractAddendumId={props.contractAddendumId}
        negotiation={selectedNegotiation}
        isOpen={isFormOpen}
        onDismiss={handleFormDismiss}
        onSave={handleSave}
        defaultSponsorId={props.defaultSponsorId}
      />

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(_, data) => { if (!data.open) handleDeleteCancel(); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('contractNegotiationList.deleteDialog.title', 'Eliminar negociación')}</DialogTitle>
            <DialogContent>
              {t(
                'contractNegotiationList.deleteDialog.message',
                '¿Está seguro que desea eliminar esta negociación? Esta acción no se puede deshacer.'
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleDeleteCancel}>
                {t('contractNegotiationList.deleteDialog.cancel', 'Cancelar')}
              </Button>
              <Button appearance="primary" onClick={handleDeleteConfirm}>
                {t('contractNegotiationList.deleteDialog.confirm', 'Eliminar')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ContractNegotiationListComponent as React.ComponentType<ComponentFunctions.OwnProps>);
